import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './Auth/AuthContext';
import { ChevronDown, User, Globe, Menu, X, Crown, LogOut, Home, Shield, Briefcase, Plane, MessageSquare, Calendar } from 'lucide-react';
import { changeLanguage } from '../i18n';
import brand from '../config/brand';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('public');
  const { user, logout, isSuperAdmin, isAdmin } = useAuth();
  const [showSolutionsMenu, setShowSolutionsMenu] = useState(false);
  const [showConnectionMenu, setShowConnectionMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Utiliser directement la langue de i18n (se met à jour automatiquement)
  const language = (i18n.language || 'fr').toUpperCase();

  // Home DNA-24 : header transparent qui devient verre sombre au scroll (ASAR/autres pages inchangés).
  const isDnaHome = brand.key === 'dna' && location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!isDnaHome) {
      setScrolled(false);
      return undefined;
    }
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isDnaHome]);

  const headerCls = isDnaHome
    ? `fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#FBF7F0]/90 backdrop-blur-xl border-b border-[#E2D9C9] shadow-sm' : 'bg-transparent border-b border-transparent'}`
    : 'bg-white shadow-lg border-b-2 border-gray-200 sticky top-0 z-50';
  const navTextCls = isDnaHome ? 'text-brand-heading hover:text-brand-primary' : 'text-brand-text hover:text-brand-primary';
  const langBtnCls = isDnaHome
    ? 'hidden sm:flex items-center text-sm lg:text-lg text-brand-heading transition-all duration-200 font-bold bg-black/5 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg hover:bg-black/10'
    : 'hidden sm:flex items-center text-sm lg:text-lg text-gray-600 hover:text-brand-primary transition-all duration-200 font-bold bg-gray-50 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg hover:bg-brand-primary/10';

  // Fermer le menu mobile lors du changement de route
  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  // Empêcher le scroll du body quand le menu mobile est ouvert
  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileMenu]);

  const toggleLanguage = () => {
    const newLang = language === 'FR' ? 'en' : 'fr';
    changeLanguage(newLang);
  };

  const handleLoginNavigation = (spaceType) => {
    navigate('/login', { state: { spaceType } });
    setShowMobileMenu(false);
  };

  const handleLogout = () => {
    logout();
    setShowMobileMenu(false);
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    return `${user.first_name} ${user.last_name}`;
  };

  const getUserRoleDisplay = () => {
    if (!user) return '';
    const roleNames = {
      super_admin: 'Super Admin',
      admin: language === 'FR' ? 'Administrateur' : 'Administrator',
      client: 'Client',
      partner: language === 'FR' ? 'Courtier' : 'Broker'
    };
    return roleNames[user.role] || user.role;
  };

  // Masqué dans l'espace admin (header unifié dédié) et sur la page de connexion
  // (expérience plein écran autonome). La vitrine publique n'est pas impactée.
  // Masqué aussi sur l'espace courtier en mode Web App mobile (appview ou < 1024px) —
  // desktop courtier inchangé.
  const courtierMobileApp = location.pathname.startsWith('/courtier-dashboard') && (
    new URLSearchParams(location.search).get('appview') === '1' ||
    (typeof window !== 'undefined' && window.innerWidth < 1024)
  );
  const adminPath = process.env.REACT_APP_ADMIN_PATH || 'gestion-csg';
  if (location.pathname.startsWith('/admin') || location.pathname === `/${adminPath}` || courtierMobileApp) {
    return null;
  }

  const solutions = [
    { id: 'particuliers', label: t('footer.individuals'), icon: Home, emoji: '🏠' },
    { id: 'professionnels', label: t('footer.professionals'), icon: Briefcase, emoji: '🏢' },
    { id: 'expatries', label: t('footer.expatriates'), icon: Globe, emoji: '🌍' },
    { id: 'voyage', label: t('footer.travel'), icon: Plane, emoji: '✈️' },
    ...(brand.key === 'dna'
      ? [{ id: 'assistance', label: t('footer.assistance', 'Assistance'), icon: Shield, emoji: '🚑' }]
      : []),
  ];

  return (
    <>
      <header className={headerCls}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20 lg:h-24">
            
            {/* Logo Section */}
            <div className="flex items-center space-x-4 lg:space-x-8">
              <Link to="/" className="flex-shrink-0 hover:scale-105 transition-transform duration-300">
                <img 
                  src={brand.logo} 
                  alt={brand.name} 
                  className="h-12 sm:h-16 lg:h-20 w-auto"
                />
              </Link>
              
              {/* Language Switch - Hidden on mobile */}
              <button
                onClick={toggleLanguage}
                className={langBtnCls}
              >
                <Globe size={14} className="mr-1 lg:mr-2" />
                <span className={`${language === 'FR' ? 'text-brand-primary' : ''}`}>FR</span>
                <span className="mx-1 text-gray-400">|</span>
                <span className={`${language === 'EN' ? 'text-brand-primary' : ''}`}>EN</span>
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              
              {/* Nos Solutions Dropdown */}
              <div 
                className="relative group"
                onMouseEnter={() => setShowSolutionsMenu(true)}
                onMouseLeave={() => setShowSolutionsMenu(false)}
              >
                <button className={`flex items-center ${navTextCls} transition-all duration-300 font-bold text-base lg:text-lg hover:scale-105 px-3 py-2 rounded-lg ${isDnaHome ? 'hover:bg-white/10' : 'hover:bg-gray-50'}`}>
                  {t('header.solutions')}
                  <ChevronDown 
                    size={18} 
                    className={`ml-2 transition-all duration-300 ${showSolutionsMenu ? 'rotate-180 text-brand-primary' : ''}`} 
                  />
                </button>
                
                {/* Solutions Dropdown Menu */}
                <div className={`absolute top-full left-0 mt-1 w-72 bg-white rounded-xl shadow-2xl border-2 border-gray-200 py-4 z-50 transition-all duration-300 ${
                  showSolutionsMenu 
                    ? 'opacity-100 visible transform translate-y-0' 
                    : 'opacity-0 invisible transform -translate-y-2'
                }`}>
                  <div className="px-3">
                    {solutions.map((solution) => (
                      <Link 
                        key={solution.id}
                        to={`/solutions/${solution.id}`}
                        className="flex items-center px-5 py-4 text-brand-text hover:bg-gradient-to-r hover:from-brand-primary/20 hover:to-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-semibold hover:translate-x-2 text-lg"
                      >
                        <span className="text-2xl mr-4">{solution.emoji}</span>
                        {solution.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Se connecter / User Menu */}
              <div 
                className="relative group"
                onMouseEnter={() => setShowConnectionMenu(true)}
                onMouseLeave={() => setShowConnectionMenu(false)}
              >
                {user ? (
                  <button className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 lg:px-6 py-2.5 lg:py-3 rounded-xl font-bold text-base lg:text-lg hover:from-green-600 hover:to-green-700 hover:shadow-xl transform hover:-translate-y-1 hover:scale-105 transition-all duration-300 flex items-center">
                    {isSuperAdmin() && <Crown size={18} className="mr-2" />}
                    <span className="max-w-[120px] truncate">{getUserDisplayName()}</span>
                    <ChevronDown 
                      size={18} 
                      className={`ml-2 transition-all duration-300 ${showConnectionMenu ? 'rotate-180' : ''}`} 
                    />
                  </button>
                ) : (
                  <button className="bg-gradient-to-r from-brand-primary to-brand-primary-hover text-white px-4 lg:px-6 py-2.5 lg:py-3 rounded-xl font-bold text-base lg:text-lg hover:from-brand-primary-hover hover:to-brand-primary-dark hover:shadow-2xl transform hover:-translate-y-1 hover:scale-105 transition-all duration-300">
                    {t('header.login')}
                    <ChevronDown 
                      size={18} 
                      className={`ml-2 inline transition-all duration-300 ${showConnectionMenu ? 'rotate-180' : ''}`} 
                    />
                  </button>
                )}
                
                {/* Connection/User Dropdown Menu */}
                <div className={`absolute top-full right-0 mt-1 w-64 bg-white rounded-xl shadow-2xl border-2 border-gray-200 py-4 z-50 transition-all duration-300 ${
                  showConnectionMenu 
                    ? 'opacity-100 visible transform translate-y-0' 
                    : 'opacity-0 invisible transform -translate-y-2'
                }`}>
                  <div className="px-3">
                    {user ? (
                      <>
                        <div className="px-5 py-3 border-b border-gray-200 mb-2">
                          <p className="font-semibold text-gray-900 truncate">{getUserDisplayName()}</p>
                          <p className="text-sm text-gray-500 flex items-center">
                            {isSuperAdmin() && <Crown size={14} className="mr-1 text-yellow-500" />}
                            {getUserRoleDisplay()}
                          </p>
                        </div>
                        
                        {user.role === 'client' && (
                          <Link 
                            to="/client-dashboard" 
                            className="flex items-center px-5 py-3 text-brand-text hover:bg-gradient-to-r hover:from-brand-primary/20 hover:to-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-semibold"
                          >
                            <User size={18} className="mr-3" />
                            {t('header.clientSpace')}
                          </Link>
                        )}
                        
                        {user.role === 'partner' && (
                          <Link 
                            to="/courtier-dashboard" 
                            className="flex items-center px-5 py-3 text-brand-text hover:bg-gradient-to-r hover:from-brand-primary/20 hover:to-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-semibold"
                          >
                            <User size={18} className="mr-3" />
                            {t('header.partnerSpace')}
                          </Link>
                        )}
                        
                        {(isAdmin() || isSuperAdmin()) && (
                          <Link 
                            to="/admin" 
                            className="flex items-center px-5 py-3 text-brand-text hover:bg-gradient-to-r hover:from-brand-primary/20 hover:to-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-semibold"
                          >
                            <Crown size={18} className="mr-3" />
                            {t('header.adminSpace')}
                          </Link>
                        )}
                        
                        
                        <div className="border-t border-gray-200 mt-2 pt-2">
                          <button 
                            onClick={handleLogout}
                            className="flex items-center w-full px-5 py-3 text-red-600 hover:bg-red-50 transition-all duration-200 rounded-xl font-semibold"
                          >
                            <LogOut size={18} className="mr-3" />
                            {t('header.logout')}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <button 
                          onClick={() => handleLoginNavigation('client')}
                          className="flex items-center w-full px-5 py-4 text-brand-text hover:bg-gradient-to-r hover:from-brand-primary/20 hover:to-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-semibold hover:translate-x-2 text-lg"
                        >
                          <User size={20} className="mr-4" />
                          {language === 'FR' ? 'Portail client' : 'Client Portal'}
                        </button>
                        <button 
                          onClick={() => handleLoginNavigation('partner')}
                          className="flex items-center w-full px-5 py-4 text-brand-text hover:bg-gradient-to-r hover:from-brand-primary/20 hover:to-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-semibold hover:translate-x-2 text-lg"
                        >
                          <Briefcase size={20} className="mr-4" />
                          {language === 'FR' ? 'Portail courtier' : 'Broker Portal'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden flex items-center justify-center w-12 h-12 bg-gradient-to-r from-brand-primary to-brand-primary-hover text-white rounded-xl hover:from-brand-primary-hover hover:to-brand-primary-dark transition-all duration-300"
              aria-label="Menu"
            >
              {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setShowMobileMenu(false)}
        />
      )}

      {/* Mobile Menu Panel */}
      <div className={`fixed top-0 right-0 h-full w-[85%] max-w-sm bg-white z-50 transform transition-transform duration-300 ease-out lg:hidden ${
        showMobileMenu ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <span className="text-lg font-bold text-brand-text">Menu</span>
            <button 
              onClick={() => setShowMobileMenu(false)}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
            >
              <X size={24} className="text-gray-600" />
            </button>
          </div>

          {/* Mobile Menu Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* User Info (if logged in) */}
            {user && (
              <div className="bg-gradient-to-r from-brand-primary/10 to-brand-primary-hover/10 rounded-xl p-4 mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-brand-primary to-brand-primary-hover rounded-full flex items-center justify-center">
                    <User size={24} className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{getUserDisplayName()}</p>
                    <p className="text-sm text-gray-500 flex items-center">
                      {isSuperAdmin() && <Crown size={12} className="mr-1 text-yellow-500" />}
                      {getUserRoleDisplay()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Language Switch */}
            <button
              onClick={toggleLanguage}
              className="flex items-center w-full text-base text-gray-600 hover:text-brand-primary transition-all duration-200 font-semibold bg-gray-50 px-4 py-3 rounded-xl hover:bg-brand-primary/10 mb-4"
            >
              <Globe size={20} className="mr-3" />
              <span className={`${language === 'FR' ? 'text-brand-primary' : ''}`}>FR</span>
              <span className="mx-2 text-gray-400">|</span>
              <span className={`${language === 'EN' ? 'text-brand-primary' : ''}`}>EN</span>
            </button>

            {/* Solutions */}
            <div className="mb-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">{t('header.ourSolutions')}</h3>
              <div className="space-y-1">
                {solutions.map((solution) => (
                  <Link 
                    key={solution.id}
                    to={`/solutions/${solution.id}`}
                    className="flex items-center px-4 py-3 text-brand-text hover:bg-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-medium"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <span className="text-xl mr-3">{solution.emoji}</span>
                    {solution.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="mb-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">{t('header.navigation')}</h3>
              <div className="space-y-1">
                <Link 
                  to="/contact"
                  className="flex items-center px-4 py-3 text-brand-text hover:bg-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-medium"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <MessageSquare size={20} className="mr-3" />
                  {t('header.contactUs')}
                </Link>
                <Link 
                  to="/rendez-vous"
                  className="flex items-center px-4 py-3 text-brand-text hover:bg-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-medium"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Calendar size={20} className="mr-3" />
                  {t('header.appointment')}
                </Link>
              </div>
            </div>

            {/* Connection / Dashboard Links */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">{t('header.myAccount')}</h3>
              {user ? (
                <div className="space-y-1">
                  {user.role === 'client' && (
                    <Link 
                      to="/client-dashboard"
                      className="flex items-center px-4 py-3 text-brand-text hover:bg-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-medium"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <Shield size={20} className="mr-3" />
                      {t('header.clientSpace')}
                    </Link>
                  )}
                  {user.role === 'partner' && (
                    <Link 
                      to="/courtier-dashboard"
                      className="flex items-center px-4 py-3 text-brand-text hover:bg-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-medium"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <Briefcase size={20} className="mr-3" />
                      {t('header.partnerSpace')}
                    </Link>
                  )}
                  {(isAdmin() || isSuperAdmin()) && (
                    <Link 
                      to="/admin"
                      className="flex items-center px-4 py-3 text-brand-text hover:bg-brand-primary/10 hover:text-brand-primary transition-all duration-200 rounded-xl font-medium"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <Crown size={20} className="mr-3" />
                      {t('header.adminSpace')}
                    </Link>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <button 
                    onClick={() => handleLoginNavigation('client')}
                    className="flex items-center w-full px-4 py-3 bg-gradient-to-r from-brand-primary to-brand-primary-hover text-white rounded-xl font-semibold"
                  >
                    <User size={20} className="mr-3" />
                    {t('header.clientPortal')}
                  </button>
                  <button 
                    onClick={() => handleLoginNavigation('partner')}
                    className="flex items-center w-full px-4 py-3 border-2 border-brand-primary text-brand-primary rounded-xl font-semibold hover:bg-brand-primary/10"
                  >
                    <Briefcase size={20} className="mr-3" />
                    {t('header.partnerPortal')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Footer */}
          {user && (
            <div className="p-4 border-t border-gray-200">
              <button 
                onClick={handleLogout}
                className="flex items-center justify-center w-full px-4 py-3 text-red-600 bg-red-50 rounded-xl font-semibold hover:bg-red-100 transition-colors"
              >
                <LogOut size={20} className="mr-2" />
                {t('header.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;
